import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications`, { headers: authHeaders() });
      setNotifications(data.notifications);
    } catch (error) { console.error('Error fetching notifications:', error); }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications/unread-count`, { headers: authHeaders() });
      setUnreadCount(data.count);
    } catch (error) { console.error('Error fetching unread count:', error); }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications/preferences`, { headers: authHeaders() });
      setPreferences(data);
    } catch (error) { console.error('Error fetching preferences:', error); }
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      fetchNotifications();
      fetchUnreadCount();
      fetchPreferences();
      const interval = setInterval(() => { fetchUnreadCount(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications, fetchUnreadCount, fetchPreferences]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {}, { headers: authHeaders() });
      setNotifications(notifications.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) { console.error('Error marking notification as read:', error); }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/read-all`, {}, { headers: authHeaders() });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) { console.error('Error marking all as read:', error); }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/notifications/preferences`, newPrefs, { headers: authHeaders() });
      setPreferences(data.preferences);
      return data;
    } catch (error) { console.error('Error updating preferences:', error); throw error; }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, preferences, fetchNotifications, markAsRead, markAllAsRead, updatePreferences }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};
